import { useCallback, useEffect, useRef } from 'react'
import { RotateCcw, ZoomIn, ZoomOut } from 'lucide-react'
import { cn } from '@mriqbox/ui-kit'

export interface ZoomMinimapProps {
    stream: MediaStream | null
    mirrored?: boolean
    scale: number
    viewportBox: { left: string; top: string; width: string; height: string }
    onZoomIn: () => void
    onZoomOut: () => void
    onSetScale: (scale: number) => void
    onReset: () => void
    onPanToNormalized: (normX: number, normY: number) => void
    className?: string
}

export function ZoomMinimap({
    stream,
    mirrored = false,
    scale,
    viewportBox,
    onZoomIn,
    onZoomOut,
    onSetScale,
    onReset,
    onPanToNormalized,
    className
}: ZoomMinimapProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const previewRef = useRef<HTMLDivElement>(null)
    const isDraggingRef = useRef(false)

    useEffect(() => {
        const video = videoRef.current
        if (!video) return
        video.srcObject = stream
        if (stream) void video.play().catch(() => undefined)
    }, [stream])

    const handlePointerCoord = useCallback((clientX: number, clientY: number) => {
        const preview = previewRef.current
        if (!preview) return
        const rect = preview.getBoundingClientRect()
        const normX = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
        const normY = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
        onPanToNormalized(normX, normY)
    }, [onPanToNormalized])

    const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        event.currentTarget.setPointerCapture(event.pointerId)
        isDraggingRef.current = true
        handlePointerCoord(event.clientX, event.clientY)
    }, [handlePointerCoord])

    const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDraggingRef.current) return
        event.preventDefault()
        event.stopPropagation()
        handlePointerCoord(event.clientX, event.clientY)
    }, [handlePointerCoord])

    const onPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (isDraggingRef.current) {
            try {
                event.currentTarget.releasePointerCapture(event.pointerId)
            } catch {
                // ignorado
            }
            isDraggingRef.current = false
        }
    }, [])

    return (
        <div
            className={cn(
                'absolute bottom-3 right-3 z-20 flex flex-col gap-2 rounded-xl border border-white/20 bg-black/90 p-2 shadow-2xl select-none',
                'animate-in fade-in zoom-in-95 duration-150',
                className
            )}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            data-testid="zoom-minimap"
        >
            {/* Preview Minimap Box */}
            <div
                ref={previewRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                className="relative h-24 w-40 cursor-crosshair overflow-hidden rounded-lg border border-white/15 bg-black/90 shadow-inner"
                title="Clique ou arraste para posicionar o foco da tela"
            >
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={cn('h-full w-full object-contain opacity-70 pointer-events-none', mirrored && '-scale-x-100')}
                />

                {/* Viewport Indicator Rectangle */}
                <div
                    style={{
                        left: viewportBox.left,
                        top: viewportBox.top,
                        width: viewportBox.width,
                        height: viewportBox.height
                    }}
                    className="absolute rounded border-2 border-primary bg-primary/25 shadow-sm shadow-primary/30 pointer-events-none transition-all duration-75"
                />
            </div>

            {/* Zoom Slider and Controls */}
            <div className="flex items-center gap-1.5 px-0.5">
                <button
                    type="button"
                    title="Diminuir zoom"
                    aria-label="Diminuir zoom"
                    onClick={onZoomOut}
                    className="flex h-5 w-5 items-center justify-center rounded text-white/70 hover:bg-white/20 hover:text-white transition-colors"
                >
                    <ZoomOut className="h-3.5 w-3.5" />
                </button>

                <input
                    type="range"
                    min={1}
                    max={5}
                    step={0.05}
                    value={scale}
                    aria-label="Nivel de zoom"
                    onChange={(e) => onSetScale(Number(e.target.value))}
                    className="h-1.5 w-20 cursor-pointer appearance-none rounded-lg bg-white/20 accent-primary focus:outline-none"
                />

                <button
                    type="button"
                    title="Aumentar zoom"
                    aria-label="Aumentar zoom"
                    onClick={onZoomIn}
                    className="flex h-5 w-5 items-center justify-center rounded text-white/70 hover:bg-white/20 hover:text-white transition-colors"
                >
                    <ZoomIn className="h-3.5 w-3.5" />
                </button>

                <div className="ml-auto flex items-center gap-1">
                    <span className="font-mono text-[11px] font-bold text-primary">
                        {Math.round(scale * 100)}%
                    </span>
                    <button
                        type="button"
                        title="Redefinir zoom (100%)"
                        aria-label="Redefinir zoom"
                        onClick={onReset}
                        className="flex h-5 w-5 items-center justify-center rounded text-white/70 hover:bg-white/20 hover:text-white transition-colors"
                    >
                        <RotateCcw className="h-3 w-3" />
                    </button>
                </div>
            </div>
        </div>
    )
}
